import {Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {map} from 'rxjs';
import {TranslatePipe} from '@ngx-translate/core';
import {GenerateCredentialResponse, LicenceServerSummary} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {ConfirmDialog} from '../../../../components/confirm-dialog/confirm-dialog';
import {ConfirmService} from '../../../../services/confirm.service';
import {LicenceServerQueryService} from './services/licence-server-query.service';
import {LicenceServerCommandService} from './services/licence-server-command.service';
import {LicenceServerDialog, LicenceServerFormValue} from './components/licence-server-dialog/licence-server-dialog';
import {LicenceServerItem} from './components/licence-server-item/licence-server-item';
import {LicenceServerCredentialsDialog} from './components/licence-server-credentials-dialog/licence-server-credentials-dialog';

@Component({
  selector: 'elcia-customer-licence-servers',
  imports: [
    TranslatePipe,
    ElciaCard,
    ElciaButton,
    ElciaDialog,
    ConfirmDialog,
    LicenceServerDialog,
    LicenceServerItem,
    LicenceServerCredentialsDialog,
  ],
  providers: [LicenceServerQueryService, LicenceServerCommandService],
  templateUrl: './customer-licence-servers.html',
  host: {class: 'flex flex-col gap-4 min-h-0 overflow-y-auto soft-scrollbar'},
})
export class CustomerLicenceServers {
  private readonly route = inject(ActivatedRoute);
  private readonly queryService = inject(LicenceServerQueryService);
  private readonly commandService = inject(LicenceServerCommandService);
  private readonly confirmService = inject(ConfirmService);

  private readonly serverForm = viewChild<LicenceServerDialog>(LicenceServerDialog);

  protected readonly customerId = toSignal(this.route.parent!.params.pipe(map(params => Number(params['id']))));

  private readonly syncCustomerId = effect(() => {
    this.queryService.customerId.set(this.customerId() ?? null);
  });

  protected readonly licenceServers = computed(() => this.queryService.licenceServers());

  protected readonly isFormDisplayed = signal(false);
  protected readonly openMode = signal<'create' | 'edit'>('create');
  protected readonly selectedServer = signal<LicenceServerSummary | null>(null);

  // Credentials are shown once, right after generation; null keeps the dialog closed.
  protected readonly credentials = signal<GenerateCredentialResponse | null>(null);

  // Server awaiting confirmation before generating (rotating) its credentials; null keeps the dialog closed.
  protected readonly pendingCredentialsServer = signal<LicenceServerSummary | null>(null);

  protected readonly isGeneratingCredentials = computed(() =>
    this.commandService.generateCredentialMutation.isPending()
  );

  protected readonly isSaving = computed(() =>
    this.commandService.createMutation.isPending() || this.commandService.updateMutation.isPending()
  );

  protected showForm(): void {
    this.selectedServer.set(null);
    this.openMode.set('create');
    this.isFormDisplayed.set(true);
  }

  protected openEditForm(server: LicenceServerSummary): void {
    this.selectedServer.set(server);
    this.openMode.set('edit');
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
    this.selectedServer.set(null);
  }

  protected onDialogSave(): void {
    this.serverForm()?.submit();
  }

  protected onFormSubmit(value: LicenceServerFormValue): void {
    if (this.openMode() === 'edit') {
      const id = this.selectedServer()?.id;
      if (id == null) return;
      this.commandService.updateMutation.mutate({
        id,
        body: {
          name: value.name,
          customerProductIds: value.customerProductIds,
        },
      });
    } else {
      const customerId = this.customerId();
      if (customerId == null) return;
      this.commandService.createMutation.mutate({
        customerId,
        name: value.name,
        customerProductIds: value.customerProductIds,
      });
    }
  }

  // Generating new credentials invalidates the previous ones, so ask for confirmation first.
  protected onGenerateCredentials(server: LicenceServerSummary): void {
    this.pendingCredentialsServer.set(server);
  }

  protected confirmGenerateCredentials(): void {
    const server = this.pendingCredentialsServer();
    if (server == null) return;
    this.commandService.generateCredentialMutation.mutate(server.id, {
      onSuccess: credentials => {
        this.credentials.set(credentials);
        this.pendingCredentialsServer.set(null);
      },
    });
  }

  protected cancelGenerateCredentials(): void {
    this.pendingCredentialsServer.set(null);
  }

  protected closeCredentials(): void {
    this.credentials.set(null);
  }

  // True while the credentials of this specific server are being generated.
  protected isGenerating(serverId: number): boolean {
    const mutation = this.commandService.generateCredentialMutation;
    return mutation.isPending() && mutation.variables() === serverId;
  }

  protected deleteServer(server: LicenceServerSummary): void {
    this.confirmService.confirm({
      title: 'licence-server.title',
      message: server.name,
      accept: () => {
        this.commandService.deleteMutation.mutate(server.id);
      },
    });
  }

  private readonly closeFormOnSuccess = effect(() => {
    const createSuccess = this.commandService.createMutation.isSuccess();
    const updateSuccess = this.commandService.updateMutation.isSuccess();
    if (createSuccess || updateSuccess) {
      this.closeForm();
    }
  });
}
